import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { menuCategoryService } from '../../../../services/menuCategoryService';
import { menuTypeService } from '../../../../services/menuTypeService';
import { CreateMenuCategoryDTO, MenuItemTypeDTO } from '../../../../types';
import { ArrowLeft, LayoutGrid, Hash, AlertCircle, Zap } from 'lucide-react';
import '../RestaurantPage.css';

import { getServerErrorMessage } from '../../../../utils/errorUtils';
interface FormData {
  name: string;
  description: string;
  menuItemTypeId: number | '';
  sortOrder: number;
  isActive: boolean;
}

const MenuCategoryCreatePage: React.FC = () => {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id: string }>();
  const { t }       = useTranslation();
  const isEdit      = Boolean(id);

  const [saving,      setSaving]      = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [types,       setTypes]       = useState<MenuItemTypeDTO[]>([]);
  const [error,       setError]       = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [formData,    setFormData]    = useState<FormData>({
    name: '', description: '', menuItemTypeId: '', sortOrder: 0, isActive: true,
  });

  useEffect(() => {
    menuTypeService.getAll().then(res => {
      const d = (res.data as any)?.data;
      setTypes(Array.isArray(d) ? d : (d?.data ?? []));
    }).catch(() => {});

    if (isEdit && id) {
      setLoadingData(true);
      menuCategoryService.getById(parseInt(id))
        .then(res => {
          const d = (res.data as any)?.data ?? res.data;
          setFormData({
            name:           d.name           ?? '',
            description:    d.description    ?? '',
            menuItemTypeId: d.menuItemTypeId ?? '',
            sortOrder:      d.sortOrder      ?? 0,
            isActive:       d.isActive       ?? true,
          });
        })
        .catch(() => setError(t('restaurant.menuCategory.failedToLoad')))
        .finally(() => setLoadingData(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(p => ({
      ...p,
      [name]: type === 'checkbox' ? checked
            : type === 'number'   ? Number(value)
            : name === 'menuItemTypeId' ? (value === '' ? '' : parseInt(value))
            : value,
    }));
    setFieldErrors(p => ({ ...p, [name]: undefined }));
  };

  const validate = () => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim())          errs.name           = t('common.required');
    if (formData.menuItemTypeId === '') errs.menuItemTypeId = t('common.required');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);
    const payload: CreateMenuCategoryDTO = {
      name:           formData.name.trim(),
      description:    formData.description.trim() || undefined,
      menuItemTypeId: formData.menuItemTypeId as number,
      sortOrder:      formData.sortOrder,
      isActive:       formData.isActive,
    };
    try {
      if (isEdit && id) await menuCategoryService.update(parseInt(id), payload);
      else              await menuCategoryService.create(payload);
      navigate('/restaurant/menu-categories');
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('restaurant.menuCategory.failedToSave'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="ims-create-body-page rest-create-page" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="rest-create-page">
        <div className="ims-create-body-page">

          {/* Top bar */}
          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/restaurant/menu-categories')}>
                <ArrowLeft size={13} />
                {t('restaurant.menuCategory.title')}
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LayoutGrid size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? t('restaurant.menuCategory.editTitle') : t('restaurant.menuCategory.createTitle')}
                </h1>
                <p className="ims-page-subtitle">
                  {isEdit ? 'Update the category details below' : 'Group menu items under a type by creating a category'}
                </p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/restaurant/menu-categories')} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</>
                  : (isEdit ? t('common.update') : t('common.save'))}
              </button>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} />
                <span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          {/* Body */}
          <div className="ims-create-body ims-create-body-single">
            <div className="ims-panel-center" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Details card */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <LayoutGrid size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('restaurant.menuCategory.details', 'Category Details')}</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  {/* Name + Type row */}
                  <div className="mi-row-2">
                    <div className={`mi-field${fieldErrors.name ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">
                        {t('restaurant.menuCategory.name')} <span className="mi-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="mi-input"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('restaurant.menuCategory.namePlaceholder', 'e.g. Starters, Mains, Soft Drinks')}
                        maxLength={100}
                        autoFocus
                      />
                      {fieldErrors.name && <span className="mi-err">{fieldErrors.name}</span>}
                    </div>

                    <div className={`mi-field${fieldErrors.menuItemTypeId ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">
                        {t('restaurant.menuCategory.type')} <span className="mi-required">*</span>
                      </label>
                      <select
                        name="menuItemTypeId"
                        className="mi-input"
                        value={formData.menuItemTypeId}
                        onChange={handleChange}
                      >
                        <option value="">{t('restaurant.menuCategory.selectType', '— Select type —')}</option>
                        {types.map(tp => (
                          <option key={tp.id} value={tp.id}>{tp.name}</option>
                        ))}
                      </select>
                      {fieldErrors.menuItemTypeId && <span className="mi-err">{fieldErrors.menuItemTypeId}</span>}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mi-field">
                    <label className="mi-label">{t('common.description')}</label>
                    <textarea
                      name="description"
                      className="mi-input mi-textarea"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      placeholder={t('restaurant.menuCategory.descriptionPlaceholder', 'Brief description of this category')}
                      maxLength={500}
                    />
                  </div>

                  {/* Sort order */}
                  <div className="mi-field" style={{ maxWidth: 140 }}>
                    <label className="mi-label">
                      <Hash size={11} className="mi-label-icon" />
                      {t('restaurant.menuType.sortOrder', 'Sort Order')}
                    </label>
                    <input
                      type="number"
                      name="sortOrder"
                      className="mi-input"
                      value={formData.sortOrder}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>
                </div>
              </div>

              {/* Status card */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Zap size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('common.status')}</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <label className={`mi-toggle-row${formData.isActive ? ' mi-toggle-on' : ''}`}>
                    <div className="mi-toggle-text">
                      <span className="mi-toggle-label">{t('common.active')}</span>
                      <span className="mi-toggle-sub">Show this category when selecting items for an order</span>
                    </div>
                    <div className={`mi-switch${formData.isActive ? ' mi-switch-on' : ''}`}
                      onClick={() => setFormData(p => ({ ...p, isActive: !p.isActive }))}>
                      <div className="mi-switch-thumb" />
                    </div>
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default MenuCategoryCreatePage;
